import { ICONS_DIR, ICON_ATLAS_COLUMNS, ICON_ATLAS_PITCH, validateIconManifest, type IconManifest } from "odyssey-shared";
import { corsFetchPath } from "../cors-address.ts";
import { FallbackLog, type AtlasImage, type IconsSnapshot } from "./art.ts";

/**
 * The page's icons (ADR-111 decisions 8, 12 and 16, and its mass cases): the icon pointer and the atlas it names,
 * loaded once when the page starts and held for the page's life, so a reconnect or a world swap fetches neither again;
 * nothing waits for them, and every place draws its glyph until the atlas has decoded. The pointer is asked for with
 * `no-cache` (a publish replaces it); the atlas is immutable under its hashed name. A pointer that does not answer, does
 * not read, or names an atlas that does not load, does not decode or is not the size its cells need leaves the page on
 * glyphs for good, said once; there is no retry, the next page load asks again. The store also holds the UI scale the
 * places choose by, which the page sets whenever the overlay's scale changes. The atlas is decoded in CORS mode at its
 * CORS address (`cors-address.ts`), since the emoji over a character hand the decoded image to WebGL, and its bare
 * address is what the HTML overlay's places draw it from.
 */

/**
 * Where the pointer lies under the asset base.
 */
export const ICON_POINTER = `${ICONS_DIR}/current.json`;

/**
 * What a place reads the page's icons through.
 */
export interface IconSource {
  /**
   * Subscribes to changes of the snapshot.
   *
   * @param listener Called after each change.
   * @returns The unsubscribe function.
   */
  subscribe(listener: () => void): () => void;
  /**
   * The atlas's state and the UI scale now; the same object until either changes.
   *
   * @returns The snapshot.
   */
  getSnapshot(): IconsSnapshot;
  /**
   * The log every place's fallbacks are said through, once per topic for the page.
   */
  readonly log: FallbackLog;
}

/**
 * What fetches; the page's `fetch` unless a test hands in another.
 */
export type IconFetcher = (url: string, init: RequestInit) => Promise<Response>;

/**
 * A decoded image: its size in its own pixels and, when decoded in the page, the image itself.
 */
export interface DecodedImage {
  readonly width: number;
  readonly height: number;
  readonly element?: HTMLImageElement;
}

/**
 * What loads and decodes an image; the page's own unless a test hands in another.
 */
export type ImageDecoder = (url: string) => Promise<DecodedImage>;

/**
 * Loads and decodes an image in the page in CORS mode, so WebGL may take it from another origin.
 *
 * @param url The image's address.
 * @returns Its size in its own pixels and the decoded image.
 * @throws Error when it does not load or decode.
 */
async function decodeImage(url: string): Promise<DecodedImage> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.src = url;
  await image.decode();
  return { width: image.naturalWidth, height: image.naturalHeight, element: image };
}

/**
 * The atlas's size its pointer needs: one page 30 cells across on the pitch, as many rows as its highest cell.
 *
 * @param manifest The pointer.
 * @returns The width and the least height in pixels.
 */
function neededSize(manifest: IconManifest): { readonly width: number; readonly height: number } {
  const last = manifest.icons.reduce((highest, icon) => Math.max(highest, icon.cell), -1);
  return { width: ICON_ATLAS_COLUMNS * ICON_ATLAS_PITCH, height: (Math.floor(last / ICON_ATLAS_COLUMNS) + 1) * ICON_ATLAS_PITCH };
}

/**
 * The page's icons.
 */
export class PageIcons implements IconSource {
  public readonly log: FallbackLog;
  private snapshot: IconsSnapshot = { atlas: { status: "loading" }, scale: 1 };
  private readonly listeners = new Set<() => void>();
  private asked = false;

  /**
   * @param fetcher What fetches the pointer.
   * @param decode What loads and decodes the atlas.
   * @param say Where a line goes; the console's warnings unless a test hands in another.
   */
  public constructor(
    private readonly fetcher: IconFetcher = (url, init) => fetch(url, init),
    private readonly decode: ImageDecoder = decodeImage,
    say: (line: string) => void = (line) => console.warn(line),
  ) {
    this.log = new FallbackLog(say);
  }

  /**
   * Loads the pointer and decodes its atlas, once for the page; asked again, it does nothing. It never throws: a
   * failure leaves the page on glyphs, said once.
   *
   * @param base The asset base the page loads from.
   * @returns When the atlas has decoded or the load has failed.
   */
  public async load(base: string): Promise<void> {
    if (this.asked) {
      return;
    }
    this.asked = true;
    try {
      const manifest = await this.pointer(base);
      const url = `${base}/${manifest.atlas}`;
      const fetched = `${base}/${corsFetchPath(manifest.atlas)}`;
      let size: DecodedImage;
      try {
        size = await this.decode(fetched);
      } catch (error) {
        throw new Error(`${fetched} did not load or decode (${error instanceof Error ? error.message : String(error)})`);
      }
      const needed = neededSize(manifest);
      if (size.width !== needed.width || size.height < needed.height) {
        throw new Error(`${fetched} is ${size.width} by ${size.height}, not ${needed.width} wide and at least ${needed.height} tall`);
      }
      const image: AtlasImage = size.element === undefined ? { url, width: size.width, height: size.height } : { url, width: size.width, height: size.height, element: size.element };
      this.set({ atlas: { status: "ready", manifest, image }, scale: this.snapshot.scale });
    } catch (error) {
      this.log.once("atlas", `item art is not drawn, glyphs are: ${error instanceof Error ? error.message : String(error)}`);
      this.set({ atlas: { status: "failed" }, scale: this.snapshot.scale });
    }
  }

  /**
   * Sets the UI scale the places choose by.
   *
   * @param scale The whole-number UI scale d.
   */
  public setScale(scale: number): void {
    if (scale !== this.snapshot.scale) {
      this.set({ atlas: this.snapshot.atlas, scale });
    }
  }

  /**
   * Subscribes to changes of the snapshot.
   *
   * @param listener Called after each change.
   * @returns The unsubscribe function.
   */
  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The atlas's state and the UI scale now.
   *
   * @returns The snapshot.
   */
  public getSnapshot = (): IconsSnapshot => this.snapshot;

  /**
   * Fetches and validates the pointer.
   *
   * @param base The asset base.
   * @returns The pointer.
   * @throws Error naming the address when it does not answer, answers with an error or does not validate.
   */
  private async pointer(base: string): Promise<IconManifest> {
    const url = `${base}/${ICON_POINTER}`;
    let response: Response;
    try {
      response = await this.fetcher(url, { cache: "no-cache" });
    } catch (error) {
      throw new Error(`${url} did not answer (${error instanceof Error ? error.message : String(error)})`);
    }
    if (!response.ok) {
      throw new Error(`${url} responded ${response.status}`);
    }
    try {
      return validateIconManifest(await response.json());
    } catch (error) {
      throw new Error(`${url} does not read (${error instanceof Error ? error.message : String(error)})`);
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The new snapshot.
   */
  private set(next: IconsSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * Icons for a surface that has none: always loading, so every place draws its glyph and nothing is said.
 */
export const NO_ICONS: IconSource = {
  subscribe: () => () => undefined,
  getSnapshot: (() => {
    const snapshot: IconsSnapshot = { atlas: { status: "loading" }, scale: 1 };
    return () => snapshot;
  })(),
  log: new FallbackLog(() => undefined),
};
